import React from 'react';
import { formatPhoneForDisplay, formatPhoneForTel } from '../utils/phoneUtils';
import { getGetStartedUrl } from '../utils/websiteUtils';

interface DuplicatePatientErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRedirectToPortal: () => void;
}

/**
 * DuplicatePatientErrorModal - A modal component for displaying duplicate patient error.
 *
 * Displays a centered modal with specific error information for when a patient
 * with the same name and date of birth already exists in the system.
 * Includes a button to redirect to the portal.
 *
 * @param isOpen - Whether the modal is visible
 * @param onClose - Function to call when modal should be closed
 * @param onRedirectToPortal - Function to call when redirect button is pressed
 */
const DuplicatePatientErrorModal: React.FC<DuplicatePatientErrorModalProps> = ({ 
  isOpen, 
  onClose, 
  onRedirectToPortal 
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="duplicate-patient-error-modal-backdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="duplicate-patient-error-modal-title"
      aria-describedby="duplicate-patient-error-modal-message"
    >
      <div
        className="duplicate-patient-error-modal-content"
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '4px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px'
          }}
          aria-label="Close error modal"
        >
          ×
        </button>

        {/* Error icon */}
        <div
          style={{
            fontSize: '48px',
            color: '#DE5829',
            textAlign: 'center',
            marginBottom: '16px'
          }}
        >
          ⚠️
        </div>

        {/* Error title */}
        <h2
          id="duplicate-patient-error-modal-title"
          style={{
            color: '#DE5829',
            fontSize: '1.25rem',
            fontWeight: '700',
            marginBottom: '12px',
            textAlign: 'center',
            marginTop: 0
          }}
        >
          Patient Already Exists
        </h2>

        {/* Error message */}
        <p
          id="duplicate-patient-error-modal-message"
          style={{
            color: '#333',
            fontSize: '1rem',
            lineHeight: '1.5',
            marginBottom: '24px',
            textAlign: 'center',
            marginTop: 0
          }}
        >
          A patient with the selected name and date of birth already exists. Please call our Intake Team at <a href={`tel:${formatPhoneForTel()}`}>{formatPhoneForDisplay()}</a> to schedule this appointment.
        </p>

        {/* Action button */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center'
        }}>
          <button
            onClick={() => window.location.href = getGetStartedUrl()}
            style={{
              backgroundColor: '#DE5829',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              minWidth: '120px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#C44A1F';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#DE5829';
            }}
          >
            Request A Call
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicatePatientErrorModal;
